import type { Client } from "../../../types";
import { Button } from "../../../components/ui/button";
import { MessageCircle, Cake, ChevronRight, Loader2 } from "lucide-react";
import { formatDate, parseBrazilDate } from "../../utils/date";
import { useLanguage } from "../../../contexts/LanguageContext";
import { clientsLabels } from "../labels";

interface Props {
  clients: Client[];
  loading: boolean;
}

export function UpcomingBirthdays({ clients, loading }: Props) {
  const today = new Date();
  const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const { lang } = useLanguage();
  const lbl = clientsLabels[lang].birthdays;

  const upcoming = clients
    .filter(c => c.birth_date)
    .map(c => {
      const birth = parseBrazilDate(c.birth_date!);
      const next = new Date(today.getFullYear(), birth.getMonth(), birth.getDate());
      if (next < todayMid) next.setFullYear(next.getFullYear() + 1);
      const diff = Math.ceil((next.getTime() - todayMid.getTime()) / (1000 * 60 * 60 * 24));
      return { ...c, next, diff };
    })
    .filter(c => c.diff >= 0 && c.diff <= 7)
    .sort((a, b) => a.diff - b.diff);

  return (
    <section className="space-y-4">
      <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-neutral-50 flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600">
            <Cake className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-bold text-neutral-800 tracking-tight">{lbl.title}</h2>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
              <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">{lbl.loading}</p>
            </div>
          ) : upcoming.length === 0 ? (
            <div className="py-4 text-center">
              <p className="text-sm font-bold text-neutral-400 uppercase tracking-widest">{lbl.none}</p>
            </div>
          ) : (
            <div className="overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide">
              <div className="flex gap-4">
                {upcoming.map(c => (
                  <div
                    key={c.id}
                    className={`p-5 min-w-[200px] rounded-2xl border transition-all relative group ${
                      c.diff === 0 
                      ? 'bg-emerald-50 border-emerald-100' 
                      : 'bg-neutral-50/50 border-neutral-100 hover:border-emerald-100 hover:bg-white'
                    }`}
                  >
                    {c.diff === 0 && (
                      <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                    )}
                    
                    <div className="space-y-1 mb-4">
                      <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                        {c.diff === 0 ? (lang === 'pt' ? 'Hoje' : 'Today') : formatDate(c.next, { day: '2-digit', month: '2-digit' })}
                      </p>
                      <p className="font-bold text-neutral-800 text-base tracking-tight leading-tight group-hover:text-emerald-700 transition-colors">
                        {c.name}
                      </p>
                    </div>

                    {c.phone && (
                      <Button
                        onClick={() =>
                          window.open(
                            `https://api.whatsapp.com/send/?phone=55${c.phone!.replace(/\D/g, '')}`,
                            "_blank"
                          )
                        }
                        className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-all active:scale-95 flex items-center justify-center gap-2"
                      >
                        <MessageCircle className="h-4 w-4" />
                        <span className="text-[10px] uppercase tracking-widest font-bold">Parabenizar</span>
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
